import { AvatarDropdown, AvatarName, Footer, PendingCounters, SelectLang } from '@/components';
import TimeDisplay from '@/components/TimeDisplay';
import type { DashboardStats } from '@/services/dashboardAdmin';
import { getStoredAdminCurrentUser, hasStoredAdminToken } from '@/utils/adminAuth';
import useResponsive from '@/hooks/useResponsive';

import { MessageOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import { Badge, Button, Space, Tooltip } from 'antd';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

const loginPath = '/user/login';

const HeaderActions: React.FC = () => {
  const { isMobile } = useResponsive();

  return (
    <Space className="bo-header-actions" size={isMobile ? 4 : 8}>
      <Tooltip title="Viber 客服">
        <Button
          className="bo-header-viber-button"
          type={isMobile ? 'text' : 'primary'}
          shape={isMobile ? 'circle' : 'default'}
          icon={<MessageOutlined />}
          aria-label="打开 Viber 客服"
          onClick={() => history.push('/viber-chat')}
        >
          {isMobile ? null : 'Viber客服'}
        </Button>
      </Tooltip>
      <PendingCounters />
      {isMobile ? null : <SelectLang />}
    </Space>
  );
};

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  dashboardStats?: DashboardStats;
  menuCollapsed?: boolean;
}> {
  const fetchUserInfo = async () => {
    // 生产 BO 没有 /api/currentUser，直接从已保存的登录态恢复，避免启动时打 404。
    return getStoredAdminCurrentUser();
  };
  // 如果不是登录页面，执行
  const { location } = history;
  const menuCollapsed =
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
      menuCollapsed,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
    menuCollapsed,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  const stats = initialState?.dashboardStats;
  const pendingMap: Record<string, number | undefined> = {
    '/finance/deposits': stats?.app_recharge_pending,
    '/finance/withdraws': stats?.app_withdraw_pending,
    '/finance/crypto-recharges': stats?.crypto_recharge_pending,
    '/finance/crypto-withdraws': stats?.crypto_withdraw_pending,
  };
  const shouldShowDot = (path?: string) => {
    if (!path) {
      return false;
    }
    const value = pendingMap[path];
    return typeof value === 'number' && value > 1;
  };

  return {
    actionsRender: () => [<HeaderActions key="header-actions" />],
    headerContentRender: () => <TimeDisplay />,
    rightContentRender: (_, dom) => dom,
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      if (
        typeof window !== 'undefined' &&
        window.matchMedia('(max-width: 767px)').matches &&
        initialState?.menuCollapsed !== true
      ) {
        setInitialState((previous) => ({
          ...previous,
          menuCollapsed: true,
        }));
      }
      // 如果没有登录且没有 token，重定向到 login
      // 有 token 但 currentUser 为空可能是临时网络故障，不强制下线
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        if (!hasStoredAdminToken()) {
          history.push(loginPath);
        }
      }
    },
    menu: {
      defaultOpenAll: false,
      autoOpen: false,
      ignoreFlatMenu: true,
      type: 'sub',
    },
    menuItemRender: (item, dom) => {
      const content = shouldShowDot(item.path) ? (
        <Badge dot color="#f5222d" offset={[6, 4]} style={{ boxShadow: 'none' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', width: '100%' }}>{dom}</span>
        </Badge>
      ) : (
        dom
      );
      if (item.path && !item.target) {
        return <Link to={item.path}>{content}</Link>;
      }
      return content;
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {process.env.NODE_ENV === 'development' ? (
            <div className="bo-setting-drawer">
              <SettingDrawer
                disableUrlParams
                enableDarkTheme
                settings={initialState?.settings}
                onSettingChange={(settings) => {
                  setInitialState((preInitialState) => ({
                    ...preInitialState,
                    settings,
                  }));
                }}
              />
            </div>
          ) : null}
        </>
      );
    },
    ...initialState?.settings,
    collapsed: initialState?.menuCollapsed,
    onCollapse: (menuCollapsed) => {
      setInitialState((previous) => ({
        ...previous,
        menuCollapsed,
      }));
    },
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
};
